import { BasicInformation } from './BasicInformation';
import { PassportInformation } from './PassportInformation';
import { VisaInfo } from './VisaInfo';
import { TouristVisaInformation } from './TouristVisaInformation';
import { FamilyVisaInformation } from './FamilyVisaInformation';
import { WorkVisaInformation } from './WorkVisaInformation';
import { BusinessVisaInformation } from './BusinessVisaInformation';
import { TransitVisaInformation } from './TransitVisaInformation';
import _ from 'lodash';
import { Base } from './db/Base';
import { DBFile } from './db/DBFile';
import { ApplicationNote } from './ApplicationNote';

export class VisaApplication extends Base {
    visaType: string = '';
    email: string = '';
    notes: ApplicationNote[];
    basicInformation: BasicInformation = new BasicInformation();
    passportInformation: PassportInformation = new PassportInformation();
    visaInfo: VisaInfo = new VisaInfo();
    touristVisaInformation?: TouristVisaInformation;
    familyVisaInformation?: FamilyVisaInformation;
    workVisaInformation?: WorkVisaInformation;
    businessVisaInformation?: BusinessVisaInformation;
    transitVisaInformation?: TransitVisaInformation;

    public constructor(init?: Partial<VisaApplication>) {
        super();
        Object.assign(this, init);
        this.notes = init?.notes ? init.notes.map(note => new ApplicationNote(note)) : [];
        this.basicInformation = new BasicInformation(init?.basicInformation);
        this.passportInformation = new PassportInformation(init?.passportInformation);
        this.visaInfo = new VisaInfo(init?.visaInfo);
        this.touristVisaInformation = init?.touristVisaInformation ? new TouristVisaInformation(init.touristVisaInformation) : undefined;
        this.familyVisaInformation = init?.familyVisaInformation ? new FamilyVisaInformation(init.familyVisaInformation) : undefined;
        this.workVisaInformation = init?.workVisaInformation ? new WorkVisaInformation(init.workVisaInformation) : undefined;
        this.businessVisaInformation = init?.businessVisaInformation ? new BusinessVisaInformation(init.businessVisaInformation) : undefined;
        this.transitVisaInformation = init?.transitVisaInformation ? new TransitVisaInformation(init.transitVisaInformation) : undefined;
    }

    validate(): string[] {
        const errors: string[] = [];

        // Validate visaType
        if (!this.visaType) {
            errors.push("Visa type is required");
        }

        // Validate baseInformation
        errors.push(...this.basicInformation.validate());

        // Validate passportInformation
        errors.push(...this.passportInformation.validate());

        // Validate visaInfo
        errors.push(...this.visaInfo.validate());

        // Validate optional visa information based on visaType
        if (this.visaType === 'tourist' && this.touristVisaInformation) {
            errors.push(...this.touristVisaInformation.validate());
        }

        if (this.visaType === 'family' && this.familyVisaInformation) {
            errors.push(...this.familyVisaInformation.validate());
        }

        if (this.visaType === 'work' && this.workVisaInformation) {
            errors.push(...this.workVisaInformation.validate());
        }

        if (this.visaType === 'business' && this.businessVisaInformation) {
            errors.push(...this.businessVisaInformation.validate());
        }

        if (this.visaType === 'transit' && this.transitVisaInformation) {
            errors.push(...this.transitVisaInformation.validate());
        }

        return errors;
    }

    static randomVisaApplication(): VisaApplication {
        return new VisaApplication({
            visaType: 'tourist',
            basicInformation: BasicInformation.randomBasicInfo(),
            passportInformation: PassportInformation.randomPassportInfo(),
            visaInfo: VisaInfo.randomVisaInfo(),
            touristVisaInformation: TouristVisaInformation.randomTouristVisaInfo(),
            familyVisaInformation: FamilyVisaInformation.randomFamilyVisaInfo(),
            workVisaInformation: WorkVisaInformation.randomWorkVisaInfo(),
            businessVisaInformation: BusinessVisaInformation.randomBusinessVisaInfo(),
            transitVisaInformation: TransitVisaInformation.randomTransitVisaInfo()
        });
    }

    async toObject(): Promise<any> {
        return {
            visaType: this.visaType,
            basicInformation: this.basicInformation.toObject(),
            passportInformation: this.passportInformation.toObject(),
            visaInfo: this.visaInfo.toObject(),
            touristVisaInformation: this.touristVisaInformation?.toObject(),
            familyVisaInformation: this.familyVisaInformation?.toObject(),
            workVisaInformation: this.workVisaInformation?.toObject(),
            businessVisaInformation: this.businessVisaInformation?.toObject(),
            transitVisaInformation: this.transitVisaInformation?.toObject(),
        }
    }

    files(): DBFile[] {
        return _.concat(
            this.basicInformation.files(),
            this.passportInformation.files(),
            this.visaInfo.files(),
            this.touristVisaInformation?.files() || [],
            this.familyVisaInformation?.files() || [],
            this.workVisaInformation?.files() || [],
            this.businessVisaInformation?.files() || [],
            this.transitVisaInformation?.files() || []
        );
    }

    toPlainObject(): any {
        const basicInformation = _.mapKeys(this.basicInformation, (value, key) => `basicInformation_${key}`);
        const passportInformation = _.mapKeys(this.passportInformation, (value, key) => `passportInformation_${key}`);
        const visaInfo = _.mapKeys(this.visaInfo, (value, key) => `visaInfo_${key}`);
        const touristVisaInformation = this.touristVisaInformation ? _.mapKeys(this.touristVisaInformation, (value, key) => `touristVisaInformation_${key}`) : {};
        const familyVisaInformation = this.familyVisaInformation ? _.mapKeys(this.familyVisaInformation, (value, key) => `familyVisaInformation_${key}`) : {};
        const workVisaInformation = this.workVisaInformation ? _.mapKeys(this.workVisaInformation, (value, key) => `workVisaInformation_${key}`) : {};
        const businessVisaInformation = this.businessVisaInformation ? _.mapKeys(this.businessVisaInformation, (value, key) => `businessVisaInformation_${key}`) : {};
        const transitVisaInformation = this.transitVisaInformation
            ? _.mapKeys(this.transitVisaInformation, (value, key) => `transitVisaInformation_${key}`)
            : {};
        return {
            ...basicInformation,
            ...passportInformation,
            ...visaInfo,
            ...touristVisaInformation,
            ...familyVisaInformation,
            ...workVisaInformation,
            ...businessVisaInformation,
            ...transitVisaInformation,
            visaType: this.visaType,
            email: this.email,
        };
    }
}